/**
 * Platform detection helper for AI For Me intake form
 * Handles URL pattern matching and platform identification
 */

import { BasePlatform, BasePlatformMetadataTaxonomyId, PlatformName } from "../constants/platform-constants";

export interface PlatformDetectionResult {
  basePlatform: BasePlatform;
  platformName: PlatformName;
  extractedData: Record<string, any>;
  basePlatformMetadataTaxonomyId?: BasePlatformMetadataTaxonomyId;
}

const PLATFORM_PATTERNS = {
  cedric: /console\.harmony\.a2z\.com\/internal-ai-assistant/i,
  partyrock: /partyrock\.aws(\.dev)?/i,
  amazonQ: /ask\.qbusiness\.aws\.dev/i,
  amazonQuick: /^https?:\/\/.*\.quicksight\.aws\.amazon\.com\//i,
  aza: /^https:\/\/atoz\.amazon\.work/i,
  mentor: /^https:\/\/mentor\.ai\.aws\.dev\//i,
  modelHosting: /\.(a2z\.com|amazon\.dev|amazon\.com|aws\.dev|aws\.com|amazon\.work)/i,
  thirdParty: /openai\.com|anthropic\.com|cohere\.com|huggingface\.co/i,
};

/**
 * Detects the base platform from a URL and extracts relevant data
 * @param url - The URL string to analyze for platform detection
 * @returns PlatformDetectionResult object with platform type, name, and extracted data
 */
export function detectPlatform(url: string): PlatformDetectionResult {
  if (PLATFORM_PATTERNS.cedric.test(url)) {
    return {
      basePlatform: BasePlatform.CEDRIC,
      platformName: PlatformName.CEDRIC,
      extractedData: extractCedricData(url),
      basePlatformMetadataTaxonomyId: BasePlatformMetadataTaxonomyId.CEDRIC,
    };
  }

  if (PLATFORM_PATTERNS.partyrock.test(url)) {
    const extractedData = extractPartyRockData(url);
    const isInternal = extractedData.internal;

    return {
      basePlatform: isInternal ? BasePlatform.PARTYROCK_INTERNAL : BasePlatform.PARTYROCK_EXTERNAL,
      platformName: isInternal ? PlatformName.PARTYROCK_INTERNAL : PlatformName.PARTYROCK_EXTERNAL,
      extractedData: extractedData,
      basePlatformMetadataTaxonomyId: isInternal
        ? BasePlatformMetadataTaxonomyId.PARTYROCK_INTERNAL
        : BasePlatformMetadataTaxonomyId.PARTYROCK_EXTERNAL,
    };
  }

  if (PLATFORM_PATTERNS.amazonQ.test(url)) {
    return {
      basePlatform: BasePlatform.AMAZON_Q,
      platformName: PlatformName.AMAZON_Q,
      extractedData: extractAmazonQData(url),
      basePlatformMetadataTaxonomyId: BasePlatformMetadataTaxonomyId.AMAZON_Q,
    };
  }

  if (PLATFORM_PATTERNS.amazonQuick.test(url)) {
    return {
      basePlatform: BasePlatform.AMAZON_QUICK,
      platformName: PlatformName.AMAZON_QUICK,
      extractedData: {},
      basePlatformMetadataTaxonomyId: BasePlatformMetadataTaxonomyId.AMAZON_QUICK,
    };
  }

  if (PLATFORM_PATTERNS.aza.test(url)) {
    return {
      basePlatform: BasePlatform.AZA,
      platformName: PlatformName.AZA,
      extractedData: {},
      basePlatformMetadataTaxonomyId: BasePlatformMetadataTaxonomyId.AZA,
    };
  }

  if (PLATFORM_PATTERNS.mentor.test(url)) {
    return {
      basePlatform: BasePlatform.MENTOR,
      platformName: PlatformName.MENTOR,
      extractedData: {},
      basePlatformMetadataTaxonomyId: BasePlatformMetadataTaxonomyId.MENTOR,
    };
  }

  if (PLATFORM_PATTERNS.modelHosting.test(url)) {
    return {
      basePlatform: BasePlatform.OTHER,
      platformName: PlatformName.MODEL_HOSTING,
      extractedData: {},
    };
  }

  if (PLATFORM_PATTERNS.thirdParty.test(url)) {
    return {
      basePlatform: BasePlatform.OTHER,
      platformName: PlatformName.THIRD_PARTY,
      extractedData: {},
    };
  }

  // Default to third party
  return {
    basePlatform: BasePlatform.OTHER,
    platformName: PlatformName.THIRD_PARTY,
    extractedData: {},
  };
}

/**
 * Extracts Cedric-specific data from a URL
 * @param url - The Cedric platform URL to parse
 * @returns Object containing extracted Cedric data (appId, dataCollections, hasDataCollections)
 */
function extractCedricData(url: string): Record<string, any> {
  const extractedData: Record<string, any> = {};

  const personaMatch = url.match(/persona=([^&]+)/);
  if (personaMatch) {
    extractedData.appId = personaMatch[1];
  }

  const dataCollectionsMatch = url.match(/data-collections=([^&]+)/);
  if (dataCollectionsMatch) {
    extractedData.dataCollections = dataCollectionsMatch[1];
    extractedData.hasDataCollections = true;
  } else {
    extractedData.hasDataCollections = false;
  }

  return extractedData;
}

/**
 * Extracts PartyRock-specific data from a URL
 * @param url - The PartyRock platform URL to parse
 * @returns Object containing extracted PartyRock data (owner, appId, internal flag)
 */
function extractPartyRockData(url: string): Record<string, any> {
  const extractedData: Record<string, any> = {};

  const partyRockMatch = url.match(/\/u\/([^/]+)\/([^/]+)/);
  if (partyRockMatch) {
    extractedData.owner = partyRockMatch[1];
    extractedData.appId = partyRockMatch[2];
  }

  extractedData.internal = url.includes("internal.partyrock.aws.dev");
  return extractedData;
}

/**
 * Extracts Amazon Q-specific data from a URL
 * @param url - The Amazon Q platform URL to parse
 * @returns Object containing extracted Amazon Q data (appId)
 */
function extractAmazonQData(url: string): Record<string, any> {
  const extractedData: Record<string, any> = {};

  const appIdMatch = url.match(/app\/([^/&#]+)/);
  if (appIdMatch) {
    extractedData.appId = appIdMatch[1];
  }

  return extractedData;
}
